import cn from 'classnames';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import Modal from 'react-modal';
import 'cropperjs/dist/cropper.css';
import { Cropper } from 'react-cropper';
import {
  ArrowDownward,
  Close,
  Cover,
  PersonIcon,
  PersonIcon2,
  Resize,
  UploadArrow,
} from '@shared/assets';
import { CommonButton, IconButton } from '@shared/ui/buttons';
import { Card } from '@shared/ui/cards';
import * as st from './styles.module.scss';

export interface ImageInputCropperProps {
  className?: string;
  value?: string | File | null;
  isValid: boolean;
  iconVariant?: 'default' | 'person' | 'cover' | 'client';
  aspect?: [number, number];
  disabled?: boolean;
  onChange?: (e: string | null) => void;
}

interface ICropper extends Cropper {
  getCroppedCanvas(options?: Cropper.GetCroppedCanvasOptions): HTMLCanvasElement;
}

export const ImageInputCropper = ({
  value,
  onChange,
  isValid,
  iconVariant = 'default',
  aspect = [4, 3],
  className,
  disabled,
}: ImageInputCropperProps): JSX.Element => {
  /**
   * TODO: Завести глобальнную css-переменную под полупрозрачный цвет (фон модалки)
   */

  const inputRef = useRef<HTMLInputElement>(null);
  const cropperRef = useRef<HTMLImageElement>(null);

  const [url, setUrl] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [image, setImage] = useState<string>('');
  const [cropData, setCropData] = useState<string>('');

  const onImageChange = (file: File): void => {
    if (file) {
      const reader = new FileReader();
      reader.onload = function (e) {
        const image = new Image();
        image.src = e.target?.result as string;
        e.target && setImage(image.src);
      };
      reader.readAsDataURL(file);
    }
  };

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target.files && event.target.files.length > 0) {
      onImageChange(event.target.files[0]);
    }
  };

  const onImageDelete = (): void => {
    setUrl('');
    setImage('');
    onChange && onChange(null);
  };

  const onCrop = (): void => {
    const imageElement: any = cropperRef?.current;
    const cropper: ICropper = imageElement?.cropper;
    setCropData(cropper.getCroppedCanvas().toDataURL('image/png'));
  };

  const handleCropDone = (): void => {
    setShowModal(false);
    setUrl(cropData);
    onChange && onChange(cropData);
  };

  useEffect(() => {
    if (!value || typeof value === 'number' || value === null) return;
    if (value instanceof File) {
      onImageChange(value);
    } else if (typeof value === 'string') {
      setUrl(value);
    }
  }, [value]);

  interface PlaceholderProps {
    Icon: ReactNode;
    text?: string;
  }

  const Placeholder = ({ Icon, text }: PlaceholderProps): JSX.Element => (
    <div className={cn(st.blank, isValid && st.blank__error)}>
      {Icon}
      {text && <p className={st.blank__text}>{text}</p>}
    </div>
  );

  const renderPlaceholder = () => {
    switch (iconVariant) {
      case 'default':
        return <Placeholder Icon={<Cover />} text="Изображение" />;
      case 'cover':
        return <Placeholder Icon={<Cover />} text="Обложка 16:9" />;
      case 'person':
        return <Placeholder Icon={<PersonIcon />} />;
      case 'client':
        return <Placeholder Icon={<PersonIcon2 className={st.iconClient} />} />;
      default:
        return null;
    }
  };

  const renderHover = () => {
    if (!url) {
      return (
        <div className={cn(st.image__hover, iconVariant === 'client' && st.image__hover_client)}>
          <div className={st.image__circle}>
            <ArrowDownward />
          </div>
        </div>
      );
    } else {
      return (
        <div className={cn(st.action, iconVariant === 'client' && st.action_client)}>
          <div
            className={cn(st.action__circle, iconVariant === 'client' && st.action__circle_client)}
            onClick={() => setShowModal(true)}
          >
            <Resize />
          </div>
        </div>
      );
    }
  };

  return (
    <>
      <div
        id={'imageCropper'}
        className={cn(
          st.image,
          isValid && st.error,
          iconVariant === 'client' && st.client,
          className
        )}
        onClick={() => !disabled && setShowModal(true)}
      >
        {url ? (
          <img
            src={url}
            className={cn(st.image__picture, iconVariant === 'client' && st.image__picture_client)}
            alt=""
          />
        ) : (
          renderPlaceholder()
        )}
        {!disabled && renderHover()}
      </div>
      <Modal
        isOpen={showModal}
        className={st.viewer}
        overlayClassName={st.overlay}
        shouldFocusAfterRender={false}
      >
        <Card className={st.modal}>
          <div className={st.modal__header}>
            <IconButton
              className={st.closeButton}
              onClick={() => setShowModal(false)}
              variant="white"
            >
              <Close />
            </IconButton>
            <span className={st.title}>Загрузка фотографии</span>
          </div>
          <div className={cn(st.cropper, image && st.cropper__uploaded)}>
            <div className={st.cropper__viewer}>
              <p className={st.viewer__title}>
                {image
                  ? 'Настройте отображение фотографии'
                  : 'Загрузите файл в формате JPG или PNG'}
              </p>

              {image ? (
                <div className={st.cropperWrapper}>
                  <Cropper
                    src={image}
                    ref={cropperRef}
                    aspectRatio={aspect[0] / aspect[1]}
                    guides={false}
                    viewMode={2}
                    crop={onCrop}
                    className={st.cropper__content}
                    minContainerHeight={10}
                    minContainerWidth={10}
                  />
                </div>
              ) : (
                <label htmlFor="fileLoader" className={st.label}>
                  <input
                    name={'fileLoader'}
                    key={url + image}
                    accept="image/*"
                    type="file"
                    onChange={onInputChange}
                    ref={inputRef}
                    className={st.input}
                  />
                  <UploadArrow className={st.inputIcon} />
                </label>
              )}
            </div>
            {image && (
              <section className={st.buttons}>
                <CommonButton variant="secondary" onClick={onImageDelete}>
                  Удалить
                </CommonButton>
                <CommonButton onClick={handleCropDone}>Готово</CommonButton>
              </section>
            )}
          </div>
        </Card>
      </Modal>
    </>
  );
};
